/* eslint-disable max-len */
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { changeAppLanguage } from '@src/i18n';
import { postUserLanguageCall } from '@src/store/actions/user';
import {
	getBrowserLanguage, getGuestLanguage, setGuestLanguage, normalizeLanguage,
	isLanguageLocked, isLanguageTransitionRoute, isPreLoginLanguageRoute, isPostLoginLanguageEnabled,
} from '@src/i18n/languagePolicy';

// Keep profile preferences separate from the production HR display override.
export default function useAppLanguage(): boolean {
	const { pathname } = useRouter();
	const { profile, postUserLanguageSideEffect } = useSelector((state: RootStateOrAny) => state.user);
	const dispatch = useDispatch();
	const signedIn = useSelector((state: RootStateOrAny) => state.session.isSignedIn);
	const [readyKey, setReadyKey] = useState<string>();
	const [pageRestoreCount, setPageRestoreCount] = useState(0);
	const attempted = useRef<string | number | null>(null);
	const wasAuthenticated = useRef(false);

	const authenticated = signedIn === true || signedIn === 'true';
	const keepLoginLanguage = pathname.replace(/\/$/, '') === '/business-login';
	const useGuestLanguage = isPreLoginLanguageRoute(pathname) &&
		(!authenticated || !isPostLoginLanguageEnabled(pathname) || keepLoginLanguage);
	const needsAccountLanguage = authenticated && !useGuestLanguage && !isLanguageLocked(pathname);
	const waitingForProfile = needsAccountLanguage && !profile?.id;
	// A guest render or a previous account preference must not mark the next account language ready.
	const accountLanguage = profile?.language == null ? getBrowserLanguage() : normalizeLanguage(profile.language);
	const languageKey = needsAccountLanguage ? `${pathname}:${profile?.id}:${accountLanguage}` : pathname;

	useEffect(() => {
		const handlePageShow = (event: PageTransitionEvent) => {
			// Cached documents keep their old React state; re-read the saved preference on return.
			if (event.persisted) setPageRestoreCount(count => count + 1);
		};
		window.addEventListener('pageshow', handlePageShow);
		return () => window.removeEventListener('pageshow', handlePageShow);
	}, []);

	useEffect(() => {
		let cancelled = false;
		const applyLanguage = language => {
			// Failed dictionary loads must not block authentication or the public page.
			changeAppLanguage(language)['catch'](() => undefined).then(() => {
				if (!cancelled) setReadyKey(languageKey);
			});
		};
		if (!authenticated) {
			attempted.current = null;
			if (wasAuthenticated.current) setGuestLanguage();
		}
		wasAuthenticated.current = authenticated;
		if (isLanguageTransitionRoute(pathname) || waitingForProfile) return undefined;
		// Login sets the session/profile before navigation finishes. Keep the outgoing
		// login screen unchanged; apply and initialize the profile on the destination.
		if (useGuestLanguage) {
			applyLanguage(getGuestLanguage());
			return () => { cancelled = true; };
		}
		let language = getGuestLanguage();
		if (isLanguageLocked(pathname)) {
			language = 'zh-CHT';
		} else if (authenticated && profile?.id) {
			language = profile.language == null ? getBrowserLanguage() : normalizeLanguage(profile.language);
			if (profile.language === null && attempted.current !== profile.id && !postUserLanguageSideEffect?.isLoading) {
				attempted.current = profile.id;
				dispatch(postUserLanguageCall({ language }));
			}
		}
		applyLanguage(language);
		return () => { cancelled = true; };
	}, [pathname, authenticated, profile?.id, profile?.language, dispatch, postUserLanguageSideEffect?.isLoading,
		languageKey, waitingForProfile, useGuestLanguage, pageRestoreCount]);

	return !waitingForProfile && readyKey === languageKey;
}
